<template>
  <div class="panel">
    <header class="page-header">
      <div>
        <h1>{{ title }}</h1>
        <p>{{ currentStore?.name || '鲸夕总店' }} · 共 {{ templateTotalCount }} 个模版</p>
      </div>
      <div class="actions">
        <button v-if="canManage && !selectedFolderId" type="button" class="btn" @click="onCreateFolder">新建文件夹</button>
        <button
          v-if="showPdfImportButton && canImport"
          type="button"
          class="btn-primary pdf-import-btn"
          :disabled="templateImportState.active"
          @click="triggerPdfImport"
        >{{ templateImportState.active ? 'PDF 分层识别中…' : '导入 PDF 模版' }}</button>
      </div>
    </header>

    <div class="info-banner">
      <div class="info-banner-left">
        <svg class="info-icon" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
          <circle cx="12" cy="12" r="10"></circle>
          <line x1="12" y1="16" x2="12" y2="12"></line>
          <line x1="12" y1="8" x2="12.01" y2="8"></line>
        </svg>
        <span class="info-banner-copy">
          {{ canManage
            ? (selectedFolderId
            ? '勾选模版可批量移出文件夹；也可以直接拖动移回模版库。'
            : '导入 PDF 后先进入母模版编辑，点击「保存母模版」后才会成为正式模版；也可随时再次编辑母模版。')
          : '点击模版可查看详情、预览或应用。' }}
        </span>
      </div>

      <!-- 批量操作栏组件 -->
      <TemplateBatchBar
        :selected-count="selectedIds.size"
        :folders="folders"
        :selected-folder-id="selectedFolderId"
        :can-delete="canDeleteAnyTemplate"
        @move-to="requestMoveTo"
        @move-out="requestMoveOut"
        @delete="deleteSelected"
        @clear="clearSelection"
      />
    </div>

    <!-- 运营与共创计划 Banner (100% 深度复刻原生组件) -->
    <div v-if="showPromoBanner && !selectedFolderId" class="promo-banner">
      <div class="promo-banner-left">
        <div class="promo-tag-pill">DESIGN STUDIO PRO</div>
        
        <div class="promo-title-row">
          <h2 class="promo-heading">
            <span class="promo-heading-brand">鲸夕智绘</span>
            <span class="promo-heading-dot">·</span>
            <span class="promo-heading-gradient">包装设计赋能计划</span>
          </h2>
        </div>
        
        <p class="promo-subtitle">让您的原创包装与视觉设计，更高效地赋能品牌与业务增长</p>
        
        <div class="promo-features-grid">
          <div class="promo-feat-item">
            <div class="promo-feat-icon-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M4 8V4h4" />
                <path d="M20 8V4h-4" />
                <path d="M4 16v4h4" />
                <path d="M20 16v4h-4" />
                <path d="M9 15l3-7 3 7" />
                <path d="M10 13h4" />
              </svg>
            </div>
            <div class="promo-feat-text">
              <span class="promo-feat-title">AI 智能生成</span>
              <span class="promo-feat-desc">高效创意输出</span>
            </div>
          </div>

          <div class="promo-feat-item">
            <div class="promo-feat-icon-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <rect x="3" y="3" width="7" height="7" rx="2" />
                <rect x="14" y="3" width="7" height="7" rx="2" />
                <rect x="3" y="14" width="7" height="7" rx="2" />
                <rect x="14" y="14" width="7" height="7" rx="2" />
              </svg>
            </div>
            <div class="promo-feat-text">
              <span class="promo-feat-title">模版丰富</span>
              <span class="promo-feat-desc">覆盖多产品模板</span>
            </div>
          </div>

          <div class="promo-feat-item">
            <div class="promo-feat-icon-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M12 3H5a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7" />
                <path d="M18.375 2.625a2.121 2.121 0 1 1 3 3L12 15l-4 1 1-4Z" />
              </svg>
            </div>
            <div class="promo-feat-text">
              <span class="promo-feat-title">自由编辑</span>
              <span class="promo-feat-desc">可视化灵活调整</span>
            </div>
          </div>

          <div class="promo-feat-item">
            <div class="promo-feat-icon-box">
              <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
                <path d="M21 16V8a2 2 0 0 0-1-1.73l-7-4a2 2 0 0 0-2 0l-7 4A2 2 0 0 0 3 8v8a2 2 0 0 0 1 1.73l7 4a2 2 0 0 0 2 0l7-4A2 2 0 0 0 21 16z" />
                <polyline points="3.27 6.96 12 12.01 20.73 6.96" />
                <line x1="12" y1="22.08" x2="12" y2="12" />
              </svg>
            </div>
            <div class="promo-feat-text">
              <span class="promo-feat-title">一键应用</span>
              <span class="promo-feat-desc">快速落地生产</span>
            </div>
          </div>
        </div>
      </div>

      <div class="promo-banner-right" aria-hidden="true">
        <img :src="promoBagMockup" alt="" class="promo-mockup-img" />
      </div>

      <button type="button" class="promo-close-btn" title="关闭横幅" @click="showPromoBanner = false">✕</button>
    </div>

    <!-- 分类胶囊标签群 -->
    <div v-if="!selectedFolderId && categories.length > 1" class="tpl-gallery-header">
      <div class="category-pills-row">
        <button
          v-for="cat in categories"
          :key="cat"
          type="button"
          class="category-pill-btn"
          :class="{ active: selectedCategory === cat }"
          @click="selectedCategory = cat"
        >{{ cat }}</button>
      </div>
    </div>

    <!-- 面包屑导航 -->
    <div v-if="selectedFolderId" class="tpl-breadcrumb">
      <button
        type="button"
        class="btn btn-sm tpl-breadcrumb-back"
        :class="{ 'is-drop-target': canManage && dropTarget === '__back__' }"
        @click="onBackClick"
        @dragover="onBackDragOver"
        @dragleave="onBackDragLeave"
        @drop="onDropToBack"
      >← 返回模版库</button>
      <span class="tpl-breadcrumb-sep">/</span>
      <strong class="tpl-breadcrumb-name" :title="currentFolder?.name || ''">{{ currentFolder?.name || '' }}</strong>
      <button
        v-if="canManage"
        type="button"
        class="btn btn-sm danger-outline tpl-breadcrumb-delete"
        @click="onDeleteFolder"
      >删除文件夹</button>
    </div>

    <!-- 模版与文件夹网格 -->
    <div class="tpl-grid">
      <!-- 文件夹卡片列表 -->
      <template v-if="!selectedFolderId">
        <TemplateFolderCard
          v-for="folder in folders"
          :key="folder.id"
          :folder="folder"
          :count="Math.max(folderTemplateCount(folder.id), Number(folder.templateCount) || 0)"
          :can-manage="canManage"
          :is-drop-target="canManage && dropTarget === folder.id"
          @click="onFolderClick"
          @rename="onRenameFolder"
          @delete="onDeleteFolder"
          @dragover="onFolderDragOver"
          @dragleave="onFolderDragLeave"
          @drop="onDropToFolder(folder, $event)"
        />
      </template>

      <!-- 模版卡片列表 -->
      <TemplateCard
        v-for="tpl in visibleTemplates"
        :key="tpl.id"
        :tpl="tpl"
        :thumb="getTplThumb(tpl)"
        :can-manage="canManage"
        :is-selected="selectedIds.has(tpl.id)"
        :can-rename="canRenameTemplate(tpl)"
        :can-delete="canDeleteTemplate(tpl)"
        :in-folder="!!selectedFolderId"
        @click="onTplClick"
        @toggle-select="onToggleSelection"
        @move-out="onMoveOut"
        @rename="onRenameTemplate"
        @delete="onDeleteTemplate"
        @dragstart="onDragStart"
        @dragend="onDragEnd"
        @thumb-error="onThumbError"
      />
    </div>

    <!-- 空状态提示 -->
    <WhaleEmpty
      v-if="!folders.length && !visibleTemplates.length"
      variant="templates"
      title="暂无模版"
      :description="canManage ? '当前店铺/分类下暂无模版，可导入 PDF 模版或新建文件夹' : '当前暂无可用模版'"
      :action-text="canManage ? '新建文件夹' : ''"
      @action="onCreateFolder"
    />

    <!-- 模版详情与使用弹窗 -->
    <TemplatePreviewModal
      :model-value="!!previewTpl"
      :tpl="previewTpl"
      :thumb="previewTpl ? getTplThumb(previewTpl) : ''"
      :loading="isOpeningTemplate"
      :can-edit-master="canManage"
      @update:model-value="previewTpl = null"
      @close="closePreview"
      @use-template="confirmUseTemplate"
      @edit-template="confirmEditMasterTemplate"
    />

    <!-- 系统通用交互确认弹窗 -->
    <SystemDialog
      :open="dialog.open"
      :title="dialog.title"
      :message="dialog.message"
      :confirm-text="dialog.confirmText"
      :danger="dialog.danger"
      :input="dialog.input"
      :input-value="dialog.inputValue"
      :error-message="dialog.errorMessage"
      :input-label="dialog.inputLabel"
      :input-placeholder="dialog.inputPlaceholder"
      @update:input-value="onDialogInput"
      @cancel="closeDialog"
      @confirm="confirmDialog"
    />

    <!-- 上传导入模版弹窗 -->
    <UploadDialog
      :open="uploadDialog.open"
      :title="uploadDialog.pdf ? '导入 PDF 模版' : '导入图片/源文件模版'"
      :description="uploadDialog.pdf ? '选择 PDF 设计稿文件，系统将识别图层并打开母模版编辑；确认无误后点击「保存母模版」才会写入模版库。' : '为模版命名，并从本机选择要导入的设计图片。'"
      name-label="模版名称"
      name-placeholder="例如：夏季包装袋模版"
      :name-value="uploadDialog.name"
      :file="uploadDialog.file"
      :accept="uploadDialog.pdf ? '.pdf,application/pdf' : 'image/*,.psd,.ai,.cdr'"
      :file-hint="uploadDialog.pdf ? '仅支持单页 PDF 模版，单文件大小建议不超过 50MB' : '支持 PNG、JPG、WEBP、SVG、PSD、AI、CDR 等格式'"
      :loading="uploadDialog.loading || templateImportState.active"
      :loading-message="templateImportState.message || (uploadDialog.pdf ? '正在进行 PDF 智能矢量分层识别与模版生成，请稍候…' : '正在上传模版，请稍候…')"
      :loading-text="templateImportState.active ? '导入中…' : '上传中…'"
      :is-pdf="uploadDialog.pdf"
      :error-message="uploadDialog.error"
      @update:name-value="onUploadNameInput"
      @update:file="uploadDialog.file = $event"
      @cancel="closeUploadDialog"
      @confirm="confirmUpload"
    />
  </div>
</template>

<script setup>
import { computed, onMounted, onUnmounted, ref, watch } from 'vue';
import { useRoute, useRouter } from 'vue-router';
import { usePlatformStore } from '@/composables/usePlatformStore';
import SystemDialog from '@/components/platform/SystemDialog.vue';
import UploadDialog from '@/components/platform/UploadDialog.vue';
import TemplateCard from '@/components/platform/templates/TemplateCard.vue';
import TemplateFolderCard from '@/components/platform/templates/TemplateFolderCard.vue';
import TemplateBatchBar from '@/components/platform/templates/TemplateBatchBar.vue';
import TemplatePreviewModal from '@/components/platform/templates/TemplatePreviewModal.vue';
import WhaleEmpty from '@/components/common/WhaleEmpty.vue';
import { DEFAULT_TEMPLATE_THUMB, resolveCdpAsset, cdpUrl } from '@/utils/platformHelpers';
import { isImageAssetUrl, resolveTemplateThumbUrl } from '@/utils/templateThumb';
import { normalizeTemplateFolderId } from '@/utils/artMapper';
import { isUnitTestRuntime } from '@/utils/runtimeEnv';

const promoBagMockup = cdpUrl('assets/brand/promo-bag-mockup.png');

const props = defineProps({
  title: { type: String, default: '模版库' },
  querySource: { type: Boolean, default: false },
});
const title = computed(() => props.title);
const apiModule = computed(() => props.querySource ? 'queryTemplates' : 'templates');
const route = useRoute();
const router = useRouter();

const {
  currentStore,
  session,
  storeTemplates,
  storeTemplateTotalCount,
  storeTemplateFolders,
  selectedTemplateFolderId,
  createTemplateFolder,
  renameTemplateFolder,
  deleteTemplateFolder,
  folderTemplateCount,
  moveTemplateToFolder,
  batchMoveTemplates,
  canManageTemplates,
  canImportPdfTemplate,
  canDeleteTemplate,
  canRenameTemplate,
  renameTemplate,
  deleteTemplate,
  useTemplate,
  editMasterTemplate,
  showToast,
  importTemplateFile,
  cancelPdfTemplateImport,
  templateImportState,
  artApiStatus,
  artModuleLoading,
  refreshTemplateLibrary,
  refreshModuleApi,
} = usePlatformStore();

const templates = computed(() => storeTemplates());
const templateTotalCount = computed(() => storeTemplateTotalCount());
const folders = computed(() => [...storeTemplateFolders()]);
const canManage = computed(() => canManageTemplates());
const showPdfImportButton = false;
const canImport = computed(() => Boolean(canImportPdfTemplate?.value));
const canDeleteAnyTemplate = computed(() => Boolean(canDeleteTemplate({ type: 'store' })));
const selectedFolderId = ref(route?.query?.folderId || selectedTemplateFolderId?.value || null);
const selectedIds = ref(new Set());

watch(
  () => route?.query?.folderId,
  (newFolderId) => {
    selectedFolderId.value = newFolderId || null;
    if (selectedTemplateFolderId) {
      selectedTemplateFolderId.value = newFolderId || null;
    }
  },
  { immediate: true }
);

watch(() => session.storeId, () => {
  selectedFolderId.value = null;
  if (selectedTemplateFolderId) selectedTemplateFolderId.value = null;
  selectedIds.value.clear();
  if (router && route?.name && route.query?.folderId) {
    const nextQuery = { ...route.query };
    delete nextQuery.folderId;
    router.replace({ query: nextQuery }).catch(() => {});
  }
});

const dropTarget = ref(null);
const suppressFolderClick = ref(false);
const suppressTplClick = ref(false);
const draggingId = ref(null);
const previewTpl = ref(null);
const isOpeningTemplate = ref(false);
const uploadDialog = ref({ open: false, pdf: false, name: '', file: null, loading: false, error: '' });
const dialog = ref({
  open: false,
  type: '',
  title: '',
  message: '',
  confirmText: '确认',
  danger: false,
  input: false,
  inputValue: '',
  inputLabel: '名称',
  inputPlaceholder: '',
  errorMessage: '',
  target: null,
});

const showPromoBanner = ref(true);
const selectedCategory = ref('全部');

const categories = computed(() => {
  const cats = new Set(['全部']);
  templates.value.forEach(t => {
    if (t.category) cats.add(t.category);
  });
  return Array.from(cats);
});

const currentFolder = computed(() =>
  folders.value.find(f => String(f.id) === String(selectedFolderId.value)) || null
);

const visibleTemplates = computed(() => {
  let list = templates.value;
  if (selectedFolderId.value) {
    const target = normalizeTemplateFolderId(selectedFolderId.value);
    list = list.filter(t => normalizeTemplateFolderId(t.folderId) === target);
  } else {
    list = list.filter(t => !normalizeTemplateFolderId(t.folderId));
  }
  if (selectedCategory.value !== '全部') {
    list = list.filter(t => t.category === selectedCategory.value);
  }
  return list;
});

function getTplThumb(tpl) {
  return resolveTemplateThumbUrl(tpl);
}

function onThumbError(e, tpl) {
  const img = e?.target;
  if (!img) return;
  const origin = String(tpl?.originFileUrl || '').trim();
  if (origin && isImageAssetUrl(origin)) {
    const originSrc = resolveCdpAsset(origin);
    if (img.src !== originSrc) {
      img.src = originSrc;
      return;
    }
  }
  if (tpl?.thumbFallback && img.src !== tpl.thumbFallback) {
    img.src = tpl.thumbFallback;
    return;
  }
  if (img.src !== DEFAULT_TEMPLATE_THUMB) {
    img.src = DEFAULT_TEMPLATE_THUMB;
  }
}

function onCreateFolder() {
  dialog.value = {
    open: true,
    type: 'create-folder',
    title: '新建模版文件夹',
    message: '创建文件夹后，可以拖动模版进行分类管理。',
    confirmText: '创建',
    danger: false,
    input: true,
    inputValue: '新建文件夹',
    inputLabel: '文件夹名称',
    inputPlaceholder: '例如：节日包装模版',
    errorMessage: '',
    target: null,
  };
}

function onDialogInput(value) {
  dialog.value.inputValue = value;
  dialog.value.errorMessage = '';
}

function onUploadNameInput(value) {
  uploadDialog.value.name = value;
  uploadDialog.value.error = '';
}

function onDeleteFolder(targetFolder = currentFolder.value) {
  const folder = targetFolder;
  if (!folder) return;
  openDeleteDialog(
    'delete-folder',
    '删除模版文件夹',
    `确定删除「${folder.name}」吗？文件夹内的模版会移回模版库，不会被删除。`,
    folder,
  );
}

function onToggleSelection(id, event) {
  if (event?.shiftKey && selectedIds.value.size) {
    const ids = visibleTemplates.value.map(t => t.id);
    const lastId = Array.from(selectedIds.value).pop();
    const start = ids.indexOf(lastId);
    const end = ids.indexOf(id);
    if (start !== -1 && end !== -1) {
      const [from, to] = [Math.min(start, end), Math.max(start, end)];
      for (let i = from; i <= to; i++) {
        selectedIds.value.add(ids[i]);
      }
      return;
    }
  }
  if (selectedIds.value.has(id)) {
    selectedIds.value.delete(id);
  } else {
    selectedIds.value.add(id);
  }
}

function clearSelection() {
  selectedIds.value.clear();
}

async function requestMoveTo(targetFolderId) {
  const ids = Array.from(selectedIds.value);
  if (!ids.length) return;
  const ok = await batchMoveTemplates(ids, targetFolderId);
  if (ok) {
    clearSelection();
    showToast(`已将 ${ids.length} 个模版移入文件夹`, 'success');
  }
}

async function requestMoveOut() {
  const ids = Array.from(selectedIds.value);
  if (!ids.length) return;
  const ok = await batchMoveTemplates(ids, null);
  if (ok) {
    clearSelection();
    showToast(`已将 ${ids.length} 个模版移出文件夹`, 'success');
  }
}

async function deleteSelected() {
  const ids = Array.from(selectedIds.value);
  if (!ids.length) return;
  if (!window.confirm(`确定删除选中的 ${ids.length} 个模版吗？`)) return;
  for (const id of ids) {
    await deleteTemplate(id);
  }
  clearSelection();
  showToast(`已删除 ${ids.length} 个模版`, 'success');
}

function onRenameFolder(folder = currentFolder.value) {
  if (!folder) return;
  dialog.value = {
    open: true,
    type: 'rename-folder',
    title: '重命名模版文件夹',
    message: '修改文件夹名称不会影响其中模版的使用。',
    confirmText: '保存',
    danger: false,
    input: true,
    inputValue: folder.name,
    inputLabel: '文件夹名称',
    inputPlaceholder: '例如：节日包装模版',
    errorMessage: '',
    target: folder,
  };
}

function onRenameTemplate(tpl) {
  if (!tpl) return;
  dialog.value = {
    open: true,
    type: 'rename-template',
    title: '重命名模版',
    message: '新名称会显示在模版库和模版选择列表中。',
    confirmText: '保存',
    danger: false,
    input: true,
    inputValue: tpl.name,
    inputLabel: '模版名称',
    inputPlaceholder: '例如：夏季手提袋模版',
    errorMessage: '',
    target: tpl,
  };
}

function onFolderClick(folderId) {
  if (suppressFolderClick.value) {
    suppressFolderClick.value = false;
    return;
  }
  selectedFolderId.value = folderId;
  if (selectedTemplateFolderId) {
    selectedTemplateFolderId.value = folderId;
  }
  clearSelection();
  if (router && route?.name) {
    router.replace({ query: { ...route.query, folderId } }).catch(() => {});
  }
}

function onBackClick() {
  selectedFolderId.value = null;
  if (selectedTemplateFolderId) {
    selectedTemplateFolderId.value = null;
  }
  clearSelection();
  if (router && route?.name && route.query?.folderId) {
    const nextQuery = { ...route.query };
    delete nextQuery.folderId;
    router.replace({ query: nextQuery }).catch(() => {});
  }
}

function onTplClick(tpl) {
  if (suppressTplClick.value) {
    suppressTplClick.value = false;
    return;
  }
  previewTpl.value = tpl;
}

function closePreview() {
  previewTpl.value = null;
}

async function confirmUseTemplate(tpl) {
  if (!tpl || isOpeningTemplate.value) return;
  isOpeningTemplate.value = true;
  try {
    closePreview();
    await useTemplate(tpl);
  } finally {
    isOpeningTemplate.value = false;
  }
}

async function confirmEditMasterTemplate(tpl) {
  if (!tpl || isOpeningTemplate.value) return;
  isOpeningTemplate.value = true;
  try {
    closePreview();
    await editMasterTemplate(tpl);
  } finally {
    isOpeningTemplate.value = false;
  }
}

function onDragStart(tpl, e) {
  if (!canManage.value) {
    e.preventDefault();
    return;
  }
  draggingId.value = tpl.id;
  if (!selectedIds.value.has(tpl.id)) {
    selectedIds.value.clear();
    selectedIds.value.add(tpl.id);
  }
  suppressTplClick.value = true;
  e.dataTransfer.effectAllowed = 'move';
  e.dataTransfer.setData('text/template-id', String(tpl.id));
  e.dataTransfer.setData('text/plain', JSON.stringify({
    type: 'template-batch',
    ids: Array.from(selectedIds.value),
  }));
}

function onDragEnd() {
  draggingId.value = null;
  dropTarget.value = null;
  suppressTplClick.value = true;
  suppressFolderClick.value = true;
  setTimeout(() => {
    suppressTplClick.value = false;
    suppressFolderClick.value = false;
  }, 400);
}

function onFolderDragOver(folderId, e) {
  if (!canManage.value || !draggingId.value) return;
  e.dataTransfer.dropEffect = 'move';
  dropTarget.value = folderId;
}

function onFolderDragLeave(folderId) {
  if (dropTarget.value === folderId) dropTarget.value = null;
}

async function onDropToFolder(folder, e) {
  const actualFolder = (folder && ('name' in folder || 'id' in folder) && !('dataTransfer' in folder))
    ? folder
    : (e && ('name' in e || 'id' in e) && !('dataTransfer' in e) ? e : null);
  const actualEvent = (e && 'dataTransfer' in e)
    ? e
    : (folder && 'dataTransfer' in folder ? folder : null);

  actualEvent?.preventDefault?.();
  actualEvent?.stopPropagation?.();
  dropTarget.value = null;
  suppressFolderClick.value = true;
  setTimeout(() => { suppressFolderClick.value = false; }, 400);
  if (!actualFolder || !canManage.value) return;

  const raw = actualEvent?.dataTransfer?.getData?.('text/plain') || '';
  if (raw) {
    try {
      const data = JSON.parse(raw);
      if (data.type === 'template-batch' && Array.isArray(data.ids) && data.ids.length) {
        const ok = await batchMoveTemplates(data.ids, actualFolder.id);
        if (ok) {
          showToast(`已将 ${data.ids.length} 个模版移入「${actualFolder.name}」`, 'success');
        }
        clearSelection();
        return;
      }
    } catch {
      /* 非 JSON，按单个模版 ID 处理 */
    }
  }

  const id = actualEvent?.dataTransfer?.getData?.('text/template-id') || raw || draggingId.value;
  if (!id || String(id).startsWith('{')) return;
  const tpl = templates.value.find(t => String(t.id) === String(id));
  if (await moveTemplateToFolder(id, actualFolder.id)) {
    showToast(`已将「${tpl?.name || '模版'}」移入「${actualFolder.name}」`, 'success');
    clearSelection();
  }
}

function onBackDragOver(e) {
  if (!canManage.value || !draggingId.value) return;
  e.preventDefault();
  dropTarget.value = '__back__';
}

function onBackDragLeave() {
  if (dropTarget.value === '__back__') dropTarget.value = null;
}

async function onDropToBack(e) {
  if (!canManage.value) return;
  e.preventDefault();
  const id = e.dataTransfer.getData('text/template-id') || e.dataTransfer.getData('text/plain') || draggingId.value;
  dropTarget.value = null;
  if (!id) return;
  const tpl = templates.value.find(t => String(t.id) === String(id));
  if (await moveTemplateToFolder(id, null)) {
    showToast(`已将「${tpl?.name || '模版'}」移出文件夹`, 'success');
  }
}

async function onMoveOut(tpl) {
  if (await moveTemplateToFolder(tpl.id, null)) {
    showToast(`已将「${tpl.name}」移出文件夹`, 'success');
  }
}

function onDeleteTemplate(tpl) {
  if (!tpl) return;
  openDeleteDialog(
    'delete-template',
    '删除模版',
    `确定删除「${tpl.name}」吗？删除后将无法在新建设计时选择该模版。`,
    tpl,
  );
}

function openDeleteDialog(type, title, message, target) {
  dialog.value = {
    open: true,
    type,
    title,
    message,
    confirmText: '确认删除',
    danger: true,
    input: false,
    inputValue: '',
    target,
  };
}

function closeDialog() {
  dialog.value.open = false;
}

async function confirmDialog() {
  const action = dialog.value;
  if (action.type === 'create-folder') {
    const name = action.inputValue.trim();
    if (!name) {
      dialog.value.errorMessage = '请输入文件夹名称';
      return;
    }
    if (storeTemplateFolders().some(folder => folder.name === name)) {
      dialog.value.errorMessage = '已有同名文件夹，请换一个名称';
      return;
    }
    const created = await createTemplateFolder(name);
    if (!created) {
      dialog.value.errorMessage = '创建失败，请检查文件夹名称或稍后重试';
      return;
    }
  } else if (action.type === 'rename-folder' && action.target) {
    const name = action.inputValue.trim();
    if (!name) {
      dialog.value.errorMessage = '请输入文件夹名称';
      return;
    }
    const ok = await renameTemplateFolder(action.target.id, name);
    if (!ok) {
      dialog.value.errorMessage = '重命名失败，请稍后重试';
      return;
    }
  } else if (action.type === 'delete-folder' && action.target) {
    await deleteTemplateFolder(action.target.id);
    if (selectedFolderId.value === action.target.id) {
      selectedFolderId.value = null;
    }
  } else if (action.type === 'rename-template' && action.target) {
    const name = action.inputValue.trim();
    if (!name) {
      dialog.value.errorMessage = '请输入模版名称';
      return;
    }
    const ok = await renameTemplate(action.target.id, name);
    if (!ok) {
      dialog.value.errorMessage = '重命名失败，请稍后重试';
      return;
    }
  } else if (action.type === 'delete-template' && action.target) {
    await deleteTemplate(action.target.id);
  }
  closeDialog();
}

function triggerImport() {
  uploadDialog.value = {
    open: true,
    pdf: false,
    name: '',
    file: null,
    loading: false,
    error: '',
  };
}

function triggerPdfImport() {
  uploadDialog.value = {
    open: true,
    pdf: true,
    name: '',
    file: null,
    loading: false,
    error: '',
  };
}

function closeUploadDialog() {
  uploadDialog.value.open = false;
}

async function confirmUpload() {
  const { file, name, pdf } = uploadDialog.value;
  if (!file) {
    uploadDialog.value.error = pdf ? '请选择 PDF 文件' : '请选择要导入的设计图片';
    return;
  }
  const cleanName = (name || file.name.replace(/\.[^.]+$/, '')).trim();
  if (!cleanName) {
    uploadDialog.value.error = '请输入模版名称';
    return;
  }
  uploadDialog.value.loading = true;
  uploadDialog.value.error = '';
  try {
    const folderId = normalizeTemplateFolderId(selectedFolderId.value) || 0;
    const imported = await importTemplateFile(file, { name: cleanName, folderId });
    if (imported?.error) {
      uploadDialog.value.error = imported.error;
      return;
    }
    if (imported) {
      closeUploadDialog();
    } else {
      uploadDialog.value.error = '导入失败，请查看提示或稍后重试';
    }
  } catch (err) {
    uploadDialog.value.error = err?.message || '导入异常，请检查网络';
  } finally {
    uploadDialog.value.loading = false;
  }
}

onMounted(async () => {
  if (!isUnitTestRuntime()) {
    try {
      if (props.querySource) {
        await refreshModuleApi?.('templates');
      } else {
        await refreshTemplateLibrary?.();
      }
    } catch (err) {
      if (import.meta.env.DEV) console.warn('[TemplatesPanel] 初始化加载模版/文件夹失败', err);
    }
  }
});

onUnmounted(() => {
  cancelPdfTemplateImport?.();
});
</script>

<style scoped>
@import './templates-panel.css';
</style>
